import { useMemo, useCallback } from "react";
import { useMutation } from "@tanstack/react-query";
import isEqual from "lodash.isequal";
import { useForm } from "@core/features/form";
import { apiRequest } from "@core/services/api/apiRequest";
import {
  type QuizResponse,
  type QuizAnswerPayload,
  type QuizFormData,
  type QuizQuestionInputType,
  buildFormSchemaFromFields,
  packFormDefaultValuesFromQuiz,
  makeQuestionSetFormFields,
  packAnswerPayload,
  makeQuizQuestionInputKey,
  quizEventBus,
  AFFILIATION_ANSWER_TYPES,
} from "@core/features/quiz";
import {
  type AffiliationEntry,
  useUpdateUserAffiliations,
  orgRoleEntriesToAffiliationInputs,
} from "@core/features/affiliations";
import { Loader } from "@themes/pollinator/ui/Loader";
import { useGetQuizResponse } from "../../hooks";
import { ProgramQuizForm } from "./ProgramQuizForm";

type BatchAnswerPayload = {
  answers: QuizAnswerPayload[];
};

type BatchAnswerResponse = {
  success: boolean;
  count: number;
  quiz_response: { id: number };
  errors?: string[];
};

export type Props = {
  /** Quiz ID to load and display */
  quizId: number;
  /** Optional callback when all answers are saved successfully */
  onSaveSuccess?: () => void;
  /** Optional callback when the user cancels editing */
  onCancel?: () => void;
  /** Whether to show the cancel button (defaults to true when form is dirty) */
  showCancelButton?: boolean;
  /** Custom primary button text */
  primaryButtonText?: string;
  /** Additional CSS classes for the container */
  className?: string;
  /** Layout direction - vertical or horizontal (default) */
  layout?: "vertical" | "horizontal";
  /** When true, disables all fields and hides the action bar */
  isReadOnly?: boolean;
  /** Additional CSS classes for the StickyActionBar */
  actionBarClassName?: string;
  /** When true, action bar buttons stretch to fill the full width */
  actionBarFullWidth?: boolean;
};

type InnerProps = Omit<Props, "quizId"> & {
  quiz: QuizResponse;
  quizResponseId: number;
  isReadOnly?: boolean;
};

/**
 * Inner component that renders the quiz form.
 * This component is only rendered when quiz data is available,
 * ensuring the form is created with correct defaultValues.
 */
function ProgramQuizFormInner({
  quiz,
  quizResponseId,
  onSaveSuccess,
  onCancel: onCancelProp,
  showCancelButton,
  primaryButtonText = "Save",
  className,
  layout = "horizontal",
  isReadOnly = false,
  actionBarClassName,
  actionBarFullWidth,
}: InnerProps) {
  const questionSets = quiz.questionSets ?? [];
  // Flatten questions for form schema building and answer submission
  const questions = quiz.questions ?? [];

  // Build form fields and schema from questions
  const fields = useMemo(
    () => makeQuestionSetFormFields(questions),
    [questions]
  );

  const schema = useMemo(() => buildFormSchemaFromFields(fields), [fields]);

  // Get default values from existing quiz response
  const defaultValues = useMemo(
    () => packFormDefaultValuesFromQuiz(quiz),
    [quiz]
  );

  const { updateAffiliationsAsync } = useUpdateUserAffiliations();

  // Mutation for saving batch answers
  const batchAnswerMutation = useMutation({
    mutationFn: async (payload: BatchAnswerPayload) => {
      return (await apiRequest(
        "/quiz/response/answers",
        "POST",
        payload
      )) as BatchAnswerResponse;
    },
    onSuccess: (data, variables) => {
      // Only emit events if all answers saved successfully (no partial failures)
      if (data.errors && data.errors.length > 0) {
        return;
      }

      const quizId = quiz.id ? Number(quiz.id) : 0;
      const responseId = data.quiz_response?.id;

      if (responseId) {
        variables.answers.forEach((answer) => {
          quizEventBus.emitEvent("quiz:answer:submitted", {
            questionId: answer.question,
            answer: answer.answer_text ?? answer.selected_quiz_choices ?? [],
            quizId,
            responseId,
          });
        });
      }
    },
  });

  // Submit all changed answers in a single batch request
  const submitAllAnswers = useCallback(
    async (data: QuizFormData) => {
      if (!quizResponseId || !questions.length) return;

      const answerPayloads: QuizAnswerPayload[] = [];

      for (const [fieldName, answer] of Object.entries(data)) {
        const question = questions.find(
          (q) => makeQuizQuestionInputKey(q.id) === fieldName
        );

        if (!question) continue;

        // Skip if answer hasn't changed
        if (isEqual(defaultValues[fieldName], answer)) continue;

        // Skip empty answers that were never filled in
        const defaultVal = defaultValues[fieldName];
        const isNewlyEmpty =
          answer === "" || (Array.isArray(answer) && answer.length === 0);
        const wasAlreadyEmpty =
          defaultVal === "" ||
          defaultVal === undefined ||
          (Array.isArray(defaultVal) && defaultVal.length === 0);
        if (isNewlyEmpty && wasAlreadyEmpty) continue;

        // For affiliation questions, save affiliations first and use returned IDs
        const isAffiliationQuestion = AFFILIATION_ANSWER_TYPES.includes(
          question.type as QuizQuestionInputType
        );

        let answerValue = answer as string | number[];

        if (isAffiliationQuestion && Array.isArray(answer)) {
          const inputs = orgRoleEntriesToAffiliationInputs(
            answer as unknown as AffiliationEntry[]
          );
          const saved = await updateAffiliationsAsync(inputs);
          answerValue = saved.map((a) => a.id);
        }

        const answerPayload = packAnswerPayload({
          question,
          answer: answerValue,
          quizResponseId,
        });

        answerPayloads.push(answerPayload);
      }

      // Only make API call if there are answers to submit
      if (answerPayloads.length > 0) {
        const result = await batchAnswerMutation.mutateAsync({
          answers: answerPayloads,
        });

        // Handle partial failures - throw error if some answers failed
        if (result.errors && result.errors.length > 0) {
          throw new Error(
            `Some answers failed to save: ${result.errors.join("; ")}`
          );
        }
      }

      onSaveSuccess?.();
    },
    [
      quizResponseId,
      questions,
      defaultValues,
      batchAnswerMutation,
      updateAffiliationsAsync,
      onSaveSuccess,
    ]
  );

  const { form, handleSubmit } = useForm({
    schema,
    defaultValues,
    onSubmit: submitAllAnswers,
  });

  const handleCancel = useCallback(() => {
    form.reset();
    onCancelProp?.();
  }, [form, onCancelProp]);

  const { isSubmitting, isValid, isDirty } = form.formState;

  return (
    <div className={className}>
      <ProgramQuizForm
        questionSets={questionSets}
        form={form}
        isDirty={isDirty}
        isValid={isValid}
        isSubmitting={isSubmitting}
        onSubmit={handleSubmit}
        onCancel={handleCancel}
        showCancelButton={showCancelButton}
        primaryButtonText={primaryButtonText}
        layout={layout}
        isReadOnly={isReadOnly}
        actionBarClassName={actionBarClassName}
        actionBarFullWidth={actionBarFullWidth}
      />
    </div>
  );
}

/**
 * Container component for ProgramQuizForm.
 * Handles quiz data fetching, form state management, and answer submission.
 *
 * Submits all changed answers at once when the form is saved (unlike the wizard
 * which submits answers one at a time as the user progresses).
 */
export function ProgramQuizFormContainer({
  quizId,
  className,
  ...props
}: Props) {
  const { quiz, quizResponse, isLoading, isError } = useGetQuizResponse({
    quizId,
  });

  // Show loader while data is loading
  if (isLoading) {
    return (
      <div className={className}>
        <Loader isFullPage />
      </div>
    );
  }

  // Show error state
  if (isError || !quiz || !quizResponse?.id) {
    return (
      <div className={className}>
        <p className="text-error-600">Failed to load quiz</p>
      </div>
    );
  }

  // Render inner component only when data is available
  // This ensures the form is created with correct defaultValues
  return (
    <ProgramQuizFormInner
      quiz={quiz}
      quizResponseId={quizResponse.id}
      className={className}
      {...props}
    />
  );
}

export default ProgramQuizFormContainer;
